import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Container,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import logo from "../images/logo1.png";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { NAVBAR_ITEMS } from "./NavbarData";

// Professional Styled Components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.95)",
  backdropFilter: "blur(20px)",
  boxShadow: "0 1px 0 rgba(193, 227, 40, 0.1)",
  borderBottom: "1px solid rgba(193, 227, 40, 0.15)",
  position: "sticky",
  zIndex: 1300,
}));

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  position: "relative",
  textDecoration: "none",
  color: "rgba(255, 255, 255, 0.75)",
  fontSize: "0.9375rem",
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "6px",
  transition: "all 0.2s ease",
  letterSpacing: "0.01em",
  "&:hover": {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
  },
  "&.active": {
    color: "#c1e328",
    fontWeight: 600,
    backgroundColor: "rgba(193, 227, 40, 0.1)",
  },
}));

const LogoImage = styled(LazyLoadImage)({
  height: "50px",
  marginRight: "10px",
  width: "auto",
  objectFit: "contain",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.8,
  },
});

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    width: 280,
    background: "rgba(0, 0, 0, 0.98)",
    backdropFilter: "blur(20px)",
    borderLeft: "1px solid rgba(193, 227, 40, 0.15)",
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
}));

const MobileNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  display: "block",
  width: "100%",
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255, 255, 255, 0.7)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    color: "#fff",
  },
}));

const NavContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

function Navbar() {
  const [showModal, setShowModal] = useState(false);
  const [currentNavbarItems, setCurrentNavbarItems] = useState([]);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const signUpNavbarItems = useMemo(() => 
    NAVBAR_ITEMS.filter((item) => item.isSignupPage), []
  );
  const loginNavbarItems = useMemo(() => 
    NAVBAR_ITEMS.filter((item) => item.isLoginPage), []
  );
  const homeNavbarItems = useMemo(() => 
    NAVBAR_ITEMS.filter((item) => item.isHomepage), []
  );

  useEffect(() => {
    let tempNavbarItems;
    const pathname = location.pathname;

    if (pathname.startsWith("/signup")) {
      tempNavbarItems = signUpNavbarItems;
    } else if (pathname.startsWith("/login")) {
      tempNavbarItems = loginNavbarItems;
    } else {
      tempNavbarItems = homeNavbarItems;
    }

    setCurrentNavbarItems(tempNavbarItems);
  }, [location.pathname, signUpNavbarItems, loginNavbarItems, homeNavbarItems]);

  return (
    <>
      <StyledAppBar position="sticky" elevation={0}>
        <Container maxWidth="xl">
            <Toolbar 
            disableGutters 
            sx={{ 
              minHeight: { xs: 64, md: 70 },
              py: 1,
            }}
          >
            {/* Logo */}
            <NavLink 
              to="/" 
              style={{ 
                textDecoration: "none", 
                display: "flex",
                alignItems: "center",
              }}
            >
              <LogoImage src={logo} alt="Logo Leading to homepage" />
            </NavLink>

            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <NavContainer>
                {currentNavbarItems.map((item, index) => (
                  <StyledNavLink
                    key={index}
                    to={item.to}
                    end={item.to === "/"}
                  >
                    {item.title}
                  </StyledNavLink>
                ))}
              </NavContainer>
            )}

            {/* Mobile Hamburger */}
            {isMobile && (
              <StyledIconButton
                onClick={openModal}
                size="large"
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </StyledIconButton>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <StyledDrawer
        anchor="right"
        open={showModal}
        onClose={closeModal}
        transitionDuration={350}
      >
        <DrawerHeader>
          <LogoImage src={logo} alt="Logo" style={{ height: "40px" }} />
          <StyledIconButton onClick={closeModal}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </StyledIconButton>
        </DrawerHeader>

        <Box sx={{ p: 2 }}>
          <List sx={{ p: 0 }}>
            {currentNavbarItems.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                <MobileNavLink to={item.to} end={item.to === "/"} onClick={closeModal}>
                  {({ isActive }) => (
                    <ListItemButton
                      sx={{
                        borderRadius: "8px",
                        py: 1.5,
                        px: 2,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                        ...(isActive && {
                          backgroundColor: "rgba(193, 227, 40, 0.1)",
                        }),
                      }}
                    >
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontSize: "0.9375rem",
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? "#c1e328" : "rgba(255, 255, 255, 0.75)",
                        }}
                      />
                    </ListItemButton>
                  )}
                </MobileNavLink>
              </ListItem>
            ))}
          </List>
        </Box>
      </StyledDrawer>
    </>
  );
}

export default Navbar;